import { en, type MessageKey, type PartialMessages } from "./messages";
import { cs } from "./locales/cs";
import { de } from "./locales/de";
import { es } from "./locales/es";
import { es419 } from "./locales/es-419";
import { fr } from "./locales/fr";
import { hu } from "./locales/hu";
import { it } from "./locales/it";
import { ja } from "./locales/ja";
import { ko } from "./locales/ko";
import { pl } from "./locales/pl";
import { ptBR } from "./locales/pt-BR";
import { ro } from "./locales/ro";
import { ru } from "./locales/ru";
import { sk } from "./locales/sk";
import { th } from "./locales/th";
import { tr } from "./locales/tr";
import { vi } from "./locales/vi";
import { zhCN } from "./locales/zh-CN";

/**
 * The languages Escape from Tarkov ships an interface in. Someone who plays
 * the game in Turkish should be able to have this in Turkish too.
 *
 * `en` is the fallback for every key a locale has not translated, so a partial
 * translation degrades to English rather than to a raw key.
 */
export const LOCALES = {
  en,
  cs,
  de,
  es,
  "es-419": es419,
  fr,
  hu,
  it,
  ja,
  ko,
  pl,
  "pt-BR": ptBR,
  ro,
  ru,
  sk,
  th,
  tr,
  vi,
  "zh-CN": zhCN,
} satisfies Record<string, PartialMessages>;

export type Locale = keyof typeof LOCALES;

/** Shown in the language picker, in each language's own words. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  cs: "Čeština",
  de: "Deutsch",
  es: "Español",
  "es-419": "Español (LatAm)",
  fr: "Français",
  hu: "Magyar",
  it: "Italiano",
  ja: "日本語",
  ko: "한국어",
  pl: "Polski",
  "pt-BR": "Português (BR)",
  ro: "Română",
  ru: "Русский",
  sk: "Slovenčina",
  th: "ไทย",
  tr: "Türkçe",
  vi: "Tiếng Việt",
  "zh-CN": "简体中文",
};

const STORAGE_KEY = "callout:locale";

function isLocale(value: string): value is Locale {
  return Object.hasOwn(LOCALES, value);
}

/**
 * Discord hands out tags like `en-GB` and `zh-TW`; browsers hand out anything.
 * Match the exact tag first, then the language part, then give up on English.
 */
export function resolveLocale(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  if (isLocale(tag)) return tag;

  const lower = tag.toLowerCase();
  for (const key of Object.keys(LOCALES)) {
    if (key.toLowerCase() === lower) return key as Locale;
  }

  const base = lower.split("-")[0] ?? "";
  // Spanish splits into two catalogues; anything not Latin American gets Spain.
  if (base === "es") return lower === "es-419" ? "es-419" : "es";
  if (base === "pt") return "pt-BR";
  if (base === "zh") return "zh-CN";
  if (isLocale(base)) return base;
  return null;
}

let current: Locale = "en";

export function getLocale(): Locale {
  return current;
}

export function setLocale(locale: Locale): void {
  current = locale;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // A private window is not a reason to fail.
  }
  document.documentElement.lang = locale;
}

/**
 * Picks a language: an explicit choice the person made here beats what Discord
 * says, which beats the browser, which beats English.
 */
export function initLocale(discordLocale?: string | null): Locale {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    stored = null;
  }

  const picked =
    resolveLocale(stored) ??
    resolveLocale(discordLocale) ??
    resolveLocale(navigator.language) ??
    resolveLocale(navigator.languages?.[0]) ??
    "en";

  current = picked;
  document.documentElement.lang = picked;
  return picked;
}

/** Substitutes `{name}` placeholders. Missing values are left alone, visibly. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const table = LOCALES[current] as PartialMessages;
  const template = table[key] ?? en[key];
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

export type { MessageKey };

/**
 * Renders whatever a failed request threw. A known message key wins; the
 * server's own sentence is the fallback, so nothing is ever swallowed.
 */
export function describeError(cause: unknown, fallbackKey: MessageKey): string {
  if (cause && typeof cause === "object" && "code" in cause) {
    const code = (cause as { code?: unknown }).code;
    if (typeof code === "string" && code in en) return t(code as MessageKey);
  }
  if (cause instanceof Error && cause.message) return cause.message;
  return t(fallbackKey);
}

/** For socket close reasons, which arrive as a bare key. */
export function describeKey(key: string, fallbackKey: MessageKey): string {
  return key in en ? t(key as MessageKey) : t(fallbackKey);
}
